/**
 * Which passes a resident may order on an object (spec §6.3.1; v1 Config.php):
 * each "duration × guest" setting is 0 (hidden), 1 (order directly) or 2 (only through an approval request).
 */
export type GuestKind = 'vehicle' | 'pedestrian';
export type Duration = 'oneTime' | 'temporary';
export type Mode = 'direct' | 'request';

const KEYS: Record<Duration, Record<GuestKind, string>> = {
  oneTime: { vehicle: 'passesEnableOneTimeForTenantsVehicle', pedestrian: 'passesEnableOneTimeForTenantsPedestrian' },
  temporary: { vehicle: 'passesEnableTemporaryForTenantsVehicle', pedestrian: 'passesEnableTemporaryForTenantsPedestrian' },
};

function level(value: unknown): 0 | 1 | 2 {
  if (value === true) return 1;
  const n = Number(value);
  return n === 1 || n === 2 ? n : 0;
}

const toMode = (l: 0 | 1 | 2): Mode | null => (l === 1 ? 'direct' : l === 2 ? 'request' : null);

export interface PassOptions {
  guestTypes: GuestKind[];
  durations(guest: GuestKind): Array<{ duration: Duration; mode: Mode }>;
  permanent: Mode | null;
}

export function passOptions(settings: Record<string, unknown>): PassOptions {
  const durations = (guest: GuestKind) =>
    (['oneTime', 'temporary'] as const).flatMap((duration) => {
      const mode = toMode(level(settings[KEYS[duration][guest]]));
      return mode ? [{ duration, mode }] : [];
    });

  const permanentLevel = level(settings.passesEnablePermanentForTenants);
  const permanent =
    permanentLevel === 1 ? 'direct' : permanentLevel === 2 && settings.enableRequestsForPasses !== false && settings.enableRequestsForPasses !== 0 ? 'request' : null;

  return {
    guestTypes: (['vehicle', 'pedestrian'] as const).filter((g) => durations(g).length > 0),
    durations,
    permanent,
  };
}

/**
 * Durations a resident may send as an invite (v1 Config.php): the guest chooses car or foot later, so a
 * duration is refused (DURATION_TYPE_RESTRICTED) only when it is off for both. Mode 2 is checked when the guest submits.
 */
export function inviteDurations(settings: Record<string, unknown>): Array<Duration | 'permanent'> {
  const result: Array<Duration | 'permanent'> = (['oneTime', 'temporary'] as const).filter(
    (d) => level(settings[KEYS[d].vehicle]) > 0 || level(settings[KEYS[d].pedestrian]) > 0,
  );
  if (level(settings.passesEnablePermanentForTenants) > 0) result.push('permanent');
  return result;
}
